// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { UserInput, UserOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { UserForm, UserValuesForm } from '@Organisms/User/UserCreate/UserForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';

interface UserCreateProps {
    onFinish: (user: UserInput) => void | Promise<UserOutput | void>;
    triggerElem?: ReactElement;
    orgId: string;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const UserCreate = ({
    onFinish,
    triggerElem,
    orgId,
    visible,
    onVisibleChange,
}: UserCreateProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = ({
        login = '',
        name = '',
        profile = '',
        organisation = '',
        type = 'Normal',
    }: UserValuesForm): void => {
        onFinish({ login: login.trim(), name: name.trim(), profile, organisation, type });
    };

    return (
        <THDrawer
            title={i('user.create.drawer.title')}
            triggerElem={triggerElem}
            className="drawer-form"
            visible={visible}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <UserForm
                    onFinish={({ another, ...values }) => {
                        onFinishHandler(values);
                        setFieldsTouched(false);

                        if (!another) {
                            toggleDrawer();
                            return;
                        }

                        form.resetFields();
                    }}
                    form={form}
                    orgId={orgId}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    isValid={isValid && fieldsTouched}
                    onCancel={() => {
                        toggleDrawer();
                        setFieldsTouched(false);
                        form.resetFields();
                    }}
                />
            )}
        </THDrawer>
    );
};
